import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CanActivateFn, Router } from '@angular/router';
import { map, take } from 'rxjs/operators';
import { AuthStateService } from '../states/auth.state';
import { LoginPromptService } from '../services/login-prompt.service';
import { AgeVerificationService, userNeedsAgeVerification } from '../services/age-verification.service';
import { AdminPreviewContentType, AdminPreviewService } from '../services/admin-preview.service';

// Use this on guest-only routes (login, register, home, etc.)
// Redirects to '/all' if user is already logged in
export const guestGuard: CanActivateFn = () => {
  const authState = inject(AuthStateService);
  const router = inject(Router);

  return authState.auth$.pipe(
    take(1),
    map(auth => {
      if (auth.isLoggedIn) {
        router.navigate(['/all']);
        return false;
      }
      return true;
    })
  );
};

// Use this on routes that require login (everything except home + auth pages).
// Logged-out users are kept where they are and shown the "Login Required" popup.
// If it is the first navigation (direct URL / refresh) they are sent to home.
//
// Logged-in users who still need VerifyMy age verification are sent to
// /age-verification first (Safeword is 18+ only). The API enforces the same
// rule, so this is UX, not the security boundary.
export const authGuard: CanActivateFn = (route, state) => {
  // Auth lives in localStorage, so the server can't know who is logged in.
  // Let SSR/prerender through; the guard runs again in the browser.
  if (!isPlatformBrowser(inject(PLATFORM_ID))) return true;

  const authState = inject(AuthStateService);
  const router = inject(Router);
  const loginPrompt = inject(LoginPromptService);

  // Admin panel preview: this tab holds a valid preview grant for exactly
  // this post/blog/event (route data `adminPreviewType`), so let it open
  // without a login. Every other page still needs one.
  const previewType = route.data?.['adminPreviewType'] as AdminPreviewContentType | undefined;
  if (previewType && inject(AdminPreviewService).findGrant(previewType, route.paramMap.get('id'))) {
    return true;
  }

  const auth = authState.getAuthSnapshot();
  if (auth?.isLoggedIn) {
    if (userNeedsAgeVerification(auth.user)) {
      inject(AgeVerificationService).rememberReturnUrl(state.url);
      return router.parseUrl('/age-verification');
    }
    return true;
  }

  loginPrompt.open(route.data?.['loginPromptLabel'] ?? 'this page');
  return router.navigated ? false : router.parseUrl('/');
};

// For the age-verification pages themselves: must be logged in, but must
// NOT require age verification (that would loop).
export const loggedInGuard: CanActivateFn = () => {
  if (!isPlatformBrowser(inject(PLATFORM_ID))) return true;

  const authState = inject(AuthStateService);
  const router = inject(Router);

  return authState.getAuthSnapshot()?.isLoggedIn ? true : router.parseUrl('/login');
};
